import React, { useEffect } from 'react';
import clsx from 'clsx';
import L from 'leaflet';
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  ZoomControl,
  useMap, // Import useMap
} from 'react-leaflet';
import { FullscreenControl } from 'react-leaflet-fullscreen';
import 'leaflet/dist/leaflet.css';
import 'react-leaflet-fullscreen/styles.css';
import './UIMap.scss';
import UISpinner from '../UISpinner';
import { useLeafletMap } from '@/hooks/useLeafletMap';

interface IGeoData {
  raw?: {
    addresstype: 'building' | 'house' | 'town' | 'village' | 'city' | 'country';
    type: 'building' | 'house' | 'town' | 'village' | 'city' | 'country';
  };
  label: string;
  x?: number;
  y?: number;
}

export interface UIMapProps {
  latitude?: number | null;
  longitude?: number | null;
  label?: string;
  streetAddress?: string;
  className?: string;
  geoData?: IGeoData[] | IGeoData | null;
}

const baseClass = 'UIMap';

// New component to handle map updates
const MapUpdater: React.FC<{ geoDataArray: IGeoData[] }> = ({ geoDataArray }) => {
  const map = useMap(); // Get the map instance

  useEffect(() => {
    // console.log('=== MapUpdater: geoDataArray changed ===', geoDataArray);
    if (geoDataArray.length > 0) {
      const validCoords = geoDataArray
        .filter(item => item.y != null && item.x != null)
        .map(item => L.latLng(item.y!, item.x!));

      if (validCoords.length > 1) {
        // console.log('MapUpdater: Fitting bounds for multiple markers');
        const bounds = L.latLngBounds(validCoords);
        map.fitBounds(bounds, { padding: [50, 50] });
      } else if (validCoords.length === 1) {
        // console.log('MapUpdater: Setting view for single marker');
        map.setView(validCoords[0], 15);
      }
    }
  }, [geoDataArray, map]); // Re-run when geoDataArray or map instance changes

  return null; // This component doesn't render anything
};


const UIMap: React.FC<UIMapProps> = ({
                                       latitude,
                                       longitude,
                                       label,
                                       streetAddress,
                                       geoData,
                                       className = '',
                                     }) => {
  const {
    mapReady,
    mapRef, // mapRef is still useful for other imperative actions if needed
    containerRef,
    geoDataArray,
    hasData,
    error,
  } = useLeafletMap({
    geoData,
    latitude,
    longitude,
    label,
  });

  if (error) {
    return (
      <div className={clsx(baseClass, className)} ref={containerRef}>
        <div className={`${baseClass}__error`}>
          <p>Failed to load map: {error}</p>
          {streetAddress && (
            <div className={`${baseClass}__location`}>
              <span className={`${baseClass}__marker`}>📍</span>
              <span>{streetAddress}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={clsx(baseClass, className)} ref={containerRef}>
      {mapReady ? (
        <MapContainer
          className={`${baseClass}__map-component`}
          // Initial center and zoom can be default or based on first item
          center={geoDataArray.length > 0 && geoDataArray[0].y && geoDataArray[0].x
            ? { lat: geoDataArray[0].y, lng: geoDataArray[0].x }
            : { lat: 43.6532, lng: -79.3832 }
          }
          zoom={geoDataArray.length > 0 ? 15 : 10} // Initial zoom
          ref={mapRef} // Keep mapRef for other potential uses
          scrollWheelZoom={false}
          dragging={false}
          touchZoom={false}
          zoomControl={false}
          placeholder={<UISpinner />}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />
          <ZoomControl position="topleft" />

          {/* Add the MapUpdater component here */}
          <MapUpdater geoDataArray={geoDataArray} />

          {geoDataArray
            .filter(geoDataItem => {
              if (geoDataItem.y == null || geoDataItem.x == null) {
                return false;
              }
              if (!geoDataItem.raw) {
                return true;
              }
              const arr = ['building', 'house'];
              const isAddressType = arr.some(
                i => geoDataItem.raw?.addresstype === i,
              );
              const isType = arr.some(i => geoDataItem.raw?.type === i);
              return isAddressType || isType;
            })
            .map((geoDataItem, index) => (
              <Marker
                key={index}
                position={{ lat: geoDataItem.y!, lng: geoDataItem.x! }}
              >
                <Popup className={`${baseClass}__map-component--popup`}>
                  {geoDataItem.label}
                </Popup>
              </Marker>
            ))}
          <FullscreenControl forceSeparateButton position={'topright'} />
        </MapContainer>
      ) : (
        <div className={`${baseClass}__placeholder`}>
          {error ? (
            <div className={`${baseClass}__content`}>
              <p>Failed to load map: {error}</p>
              {streetAddress && (
                <div className={`${baseClass}__location`}>
                  <span className={`${baseClass}__marker`}>📍</span>
                  <span>{streetAddress}</span>
                </div>
              )}
            </div>
          ) : (
            <UISpinner />
          )}
        </div>
      )}
    </div>
  );
};

export default UIMap;